// v10/src/components/admin/inventory/InventoryFiltersBar.js
//
// Toolbar de filtros del listado de Stock (A8) — Tarea 1 (lectura).
// Search con debounce de 300ms, multi-select por categoría/tracking_mode
// y toggle de "solo stock bajo". Botón "Limpiar" si algún filtro está activo.

import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, X, Filter as FilterIcon, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CATEGORY_KEYS } from './categoryIcons';

const TRACKING_MODES = ['quantity', 'serialized'];

const SEARCH_DEBOUNCE_MS = 300;

const InventoryFiltersBar = ({ filters, onChange, darkMode }) => {
  const { t } = useTranslation('admin');

  // El input mantiene su propio state para debounce — no rebota cada tecla
  // contra el parent.
  const [searchInput, setSearchInput] = useState(filters?.search || '');
  const debounceRef = useRef(null);
  const lastEmittedRef = useRef(filters?.search || '');

  // Si el filtro de search cambia desde afuera (ej: clear), sincronizamos
  // el input local sin disparar el debounce de vuelta.
  useEffect(() => {
    if ((filters?.search || '') !== lastEmittedRef.current) {
      setSearchInput(filters?.search || '');
      lastEmittedRef.current = filters?.search || '';
    }
  }, [filters?.search]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const next = searchInput.trim();
      if (next !== lastEmittedRef.current) {
        lastEmittedRef.current = next;
        onChange?.({ ...filters, search: next });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput, filters, onChange]);

  const handleCategoryChange = (e) => {
    const value = e.target.value || '';
    onChange?.({ ...filters, category: value });
  };

  const handleTrackingChange = (e) => {
    const value = e.target.value || '';
    onChange?.({ ...filters, tracking_mode: value });
  };

  const handleLowStockToggle = () => {
    onChange?.({ ...filters, low_stock: !filters?.low_stock });
  };

  const handleClear = () => {
    setSearchInput('');
    lastEmittedRef.current = '';
    onChange?.({ search: '', category: '', tracking_mode: '', low_stock: false });
  };

  const hasActive = !!(filters?.search || filters?.category || filters?.tracking_mode || filters?.low_stock);

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';

  const inputBase = {
    padding: '10px 14px',
    borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.04)' : 'white',
    color: fgPrimary,
    fontFamily: 'inherit',
    fontSize: 14,
    outline: 'none',
  };

  const lowStockActiveBg  = 'rgba(234,88,12,0.14)';
  const lowStockActiveCol = '#ea580c';

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
        padding: '12px 14px',
        borderRadius: 16,
        background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
        border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
      }}
    >
      {/* Search */}
      <div
        style={{
          position: 'relative',
          flex: '1 1 220px',
          minWidth: 200,
        }}
      >
        <Search
          style={{
            width: 16, height: 16, color: fgFaint,
            position: 'absolute', top: '50%', left: 12, transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('inventory_filters_search_placeholder')}
          style={{
            ...inputBase,
            paddingLeft: 36,
            paddingRight: searchInput ? 36 : 14,
            width: '100%',
            boxSizing: 'border-box',
          }}
          aria-label={t('inventory_filters_search_placeholder')}
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput('')}
            style={{
              position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 4, borderRadius: 8, display: 'flex', color: fgMuted,
            }}
            aria-label="clear"
          >
            <X style={{ width: 14, height: 14 }} />
          </button>
        )}
      </div>

      {/* Categoría */}
      <select
        value={filters?.category || ''}
        onChange={handleCategoryChange}
        style={{ ...inputBase, cursor: 'pointer', minWidth: 160 }}
        aria-label={t('inventory_filters_category_label')}
      >
        <option value="">{t('inventory_filters_category_all')}</option>
        {CATEGORY_KEYS.map((key) => (
          <option key={key} value={key}>
            {t(`inventory_categories_${key}`)}
          </option>
        ))}
      </select>

      {/* Tracking mode */}
      <select
        value={filters?.tracking_mode || ''}
        onChange={handleTrackingChange}
        style={{ ...inputBase, cursor: 'pointer', minWidth: 170 }}
        aria-label={t('inventory_filters_tracking_label')}
      >
        <option value="">{t('inventory_filters_tracking_all')}</option>
        {TRACKING_MODES.map((mode) => (
          <option key={mode} value={mode}>
            {t(`inventory_filters_tracking_${mode}`)}
          </option>
        ))}
      </select>

      {/* Low stock toggle */}
      <motion.button
        type="button"
        whileTap={{ scale: 0.97 }}
        onClick={handleLowStockToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '9px 14px', borderRadius: 12, border: 'none', cursor: 'pointer',
          background: filters?.low_stock
            ? lowStockActiveBg
            : (darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9'),
          color: filters?.low_stock
            ? lowStockActiveCol
            : (darkMode ? 'rgba(255,255,255,0.75)' : '#475569'),
          fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
        }}
        aria-pressed={!!filters?.low_stock}
      >
        <AlertTriangle style={{ width: 14, height: 14 }} />
        {t('inventory_filters_low_stock_label')}
      </motion.button>

      {/* Clear filters */}
      {hasActive && (
        <motion.button
          type="button"
          whileTap={{ scale: 0.97 }}
          onClick={handleClear}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 14px', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: 'transparent',
            color: darkMode ? 'rgba(255,255,255,0.65)' : '#475569',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
          }}
        >
          <FilterIcon style={{ width: 14, height: 14 }} />
          {t('inventory_filters_clear')}
        </motion.button>
      )}
    </div>
  );
};

export default InventoryFiltersBar;
